import type { GetStaticPropsContext, GetStaticPropsResult } from 'next/types'
import type {
  AllPagesQuery,
  AllProjectSlugsQuery,
  AllProjectSlugsQueryVariables,
  ProjectQuery,
  ProjectQueryVariables,
} from '@/.generated/sanityTypes/graphql'
import type {
  Advisor,
  AllTrustedAdvisorsQuery,
  TrustedAdvisorQuery,
  TrustedAdvisorQueryVariables,
} from '@/.generated/tbfTypes/graphql'

import React, { useState } from 'react'
import { useRouter } from 'next/router'
import { useTranslations } from 'next-intl'

import Layout from '@/components/layout'
import Card from '@/components/card'
import ProjectModal from '@/components/projectModal'
import BlockContent from '@/components/blockContent'
import { BlackButtonLink } from '@/components/buttons'
import Contact from '@/components/contactSimple'
import Img from '@/components/Img'
import withNewLines from '@/lib/withNewLines'
import { getAllPages, getPageQueryVariables } from '@/lib/query'
import { request } from '@/lib/sanityClient'
import { request as requestTbf } from '@/lib/tbfClient'
import { getTypedLocale, isLocalString } from '../../lib/typeHelper'
import {
  project as projectQuery,
  allProjectSlugs as allProjectSlugsQuery,
} from './[slug].sanity.graphql'
import { trustedAdvisor as trustedAdvisorQuery } from '@/pages/contact-partners/[slug].tbf.graphql'
import { createLookupMap } from '@/lib/createLookupMap'
import type { ImageUrlList } from '@/lib/next.tbf-helper'
import { getEmployeesImageURLs } from '@/lib/next.tbf-helper'

type ProjectProps = {
  data: ProjectQuery & {
    allEmployeesImageURLs: ImageUrlList
  }
  messages: unknown
  isPreview: boolean
  allPages: AllPagesQuery
  advisorsData: AllTrustedAdvisorsQuery['trustedAdvisors']
}

const Project = ({ data, isPreview, allPages, advisorsData }: ProjectProps) => {
  const router = useRouter()
  const locale = getTypedLocale(useRouter().locale)
  const t = useTranslations('projects')
  const project = data?.allProject[0]
  const advisorPicturesMap = createLookupMap(
    data?.allEmployeesImageURLs,
    'alias',
  )
  const advisorsMap = createLookupMap(
    advisorsData as Advisor[],
    'activeDirectoryId',
  )
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)
  const squareImageSize = 100

  if (router.isFallback) {
    return <div>Loading...</div>
  }

  return (
    <Layout
      allPages={allPages}
      isPreview={isPreview}
      title={
        (isLocalString(project?.title) && project?.title[locale]) || undefined
      }
      headerFiles={project.image ? [project.image] : []}
    >
      <ProjectModal
        key={project._id}
        project={project}
        isOpen={isModalOpen}
        imageIndex={selectedImageIndex}
        setSelectedImageIndex={setSelectedImageIndex}
        onClose={() => {
          setIsModalOpen(false)
        }}
      />
      <Card className="mx-auto max-w-5xl">
        {/* TITLE */}
        <div className="text-center">
          <h2 className="xs:text-4xl mt-4 text-3xl leading-none text-red-500 md:mt-6 md:text-5xl lg:mt-8 lg:text-6xl">
            {withNewLines(
              isLocalString(project.title) ? project.title[locale] : '',
            )}
          </h2>
          <h3 className="mx-auto mt-2 text-xl leading-tight text-red-500 md:w-10/12 md:text-2xl lg:mt-3">
            {isLocalString(project.location) && project.location[locale]}
          </h3>
        </div>
        <div className="flex flex-wrap">
          <div className="text-md w-full md:w-2/3 md:pr-10 md:text-lg">
            {/* LEFT COLUMN */}

            {/* CONTENT */}
            <div className="mt-8 md:mr-16">
              {project.content && (
                <BlockContent blocks={project.content[`${locale}Raw`]} />
              )}
            </div>

            <div className="mr-16 mt-8 hidden md:block">
              {/* Services */}

              {project.projectServices &&
                project.projectServices.length > 0 && (
                  <div className="mt-4 text-left">
                    <h2 className="my-4 text-red-500">{t('services')}</h2>
                    {project.projectServices.map((service: any) => (
                      <BlackButtonLink
                        href={`/projects?serviceFilter=${service._id}`}
                        key={service._id}
                        className="mb-3 mr-3"
                      >
                        {service.title[locale]}
                      </BlackButtonLink>
                    ))}
                  </div>
                )}

              {/* Domains */}
              {project.projectDomains && project.projectDomains.length > 0 && (
                <div className="mt-4 text-left">
                  <h2 className="my-4 text-red-500">{t('domains')}</h2>
                  {project.projectDomains.map((domain: any) => (
                    <BlackButtonLink
                      href={`/projects?domainFilter=${domain._id}`}
                      key={domain._id}
                      className="mb-3 mr-3"
                    >
                      {domain.title[locale]}
                    </BlackButtonLink>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <div className="text-md w-full text-left md:w-1/3 md:text-lg">
            {/* IMAGES */}
            {project.projectImages && project.projectImages.length > 0 ? (
              <div>
                <h4 className="mb-4 mt-2 text-red-500 sm:mt-8">
                  {t('impressions')}
                </h4>
                <div className="no-wrap -mt-3 flex overflow-auto sm:flex-wrap">
                  {project.projectImages.map((image: any, index: number) => (
                    <div
                      className="group relative mr-3 mt-3"
                      key={image._key}
                      onClick={() => {
                        setSelectedImageIndex(index)
                        setIsModalOpen(true)
                      }}
                    >
                      <Img
                        objectFit="cover"
                        sizes={`${squareImageSize * 2}px`}
                        image={image}
                        style={{
                          height: squareImageSize,
                          width: squareImageSize,
                        }}
                        className="m4 cursor-pointer"
                      />
                      <div className="absolute bottom-0 mb-5 hidden w-full justify-center group-hover:flex">
                        <div
                          className="cursor-pointer bg-yellow-500 px-2 text-sm text-white"
                          style={{
                            color: '#332e00',
                          }}
                        >
                          {t('enlarge')}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {/* TRUSTED ADVISORS */}
            {project.advisors && project.advisors !== '' && (
              <div>
                <h4 className="mb-4 mt-10 text-red-500 sm:mt-16">
                  {t('team')}
                </h4>
                <div className="no-wrap -mt-3 flex overflow-auto sm:flex-wrap">
                  {(project.advisors || '')
                    .split(',')
                    .map((adId: string) => ({
                      employee: advisorsMap?.[adId],
                      profilePicture: advisorPicturesMap?.[adId]?.imageUrl,
                    }))
                    .filter((x) => !!x.employee && !!x.profilePicture)
                    .map((person) => (
                      <Contact
                        key={person.employee?.activeDirectoryId}
                        person={person.employee}
                        profilePictureUrl={person.profilePicture}
                      />
                    ))}
                </div>
              </div>
            )}

            <div className="mr-16 mt-8 md:hidden">
              {/* Domains */}
              {project.projectDomains && project.projectDomains.length > 0 && (
                <div className="mt-4 text-left">
                  <h2 className="my-4 text-red-500">{t('services')}</h2>
                  {project.projectDomains.map((domain: any) => (
                    <BlackButtonLink
                      href={`/projects?domainFilter=${domain._id}`}
                      key={domain._id}
                      className="mb-3 mr-3"
                    >
                      {domain.title[locale]}
                    </BlackButtonLink>
                  ))}
                </div>
              )}

              {/* Services */}
              {project.projectServices &&
                project.projectServices.length > 0 && (
                  <div className="mt-4 text-left">
                    <h2 className="my-4 text-red-500">{t('domains')}</h2>
                    {project.projectServices.map((service: any) => (
                      <BlackButtonLink
                        href={`/projects?serviceFilter=${service._id}`}
                        key={service._id}
                        className="mb-3 mr-3"
                      >
                        {service.title[locale]}
                      </BlackButtonLink>
                    ))}
                  </div>
                )}
            </div>
          </div>
        </div>
      </Card>

      <div className="text-center">
        <BlackButtonLink href={`/projects`}>{t('back')}</BlackButtonLink>
      </div>
    </Layout>
  )
}

export async function getStaticPaths() {
  const pageData = await request<
    AllProjectSlugsQuery,
    AllProjectSlugsQueryVariables
  >(allProjectSlugsQuery)
  const slugs = pageData.data.allProject.map((project) => project.slug?.current)

  return {
    paths: slugs.map((slug) => ({ params: { slug } })),
    fallback: true,
  }
}

export async function getStaticProps(
  context: GetStaticPropsContext,
): Promise<GetStaticPropsResult<ProjectProps>> {
  if (!context.params?.slug || Array.isArray(context.params)) {
    throw new Error(`slug is not a string, slug is: '${context.params?.slug}'`)
  }
  const isPreview = context.preview || false
  const queryVariables = getPageQueryVariables(
    context.params.slug.toString()!,
    'slug',
  )

  const pageData = await request<ProjectQuery, ProjectQueryVariables>(
    projectQuery,
    queryVariables,
    isPreview,
  )

  if (pageData.errors)
    throw new Error(`graphql Error occured: ${pageData.errors[0].message}`)

  if (!pageData.data?.allProject.length) return { notFound: true }
  const allProjectAdvisors =
    pageData?.data.allProject.map((p) => p.advisors?.split(',')).flat() || []
  const allEmployeesImageURLs = await getEmployeesImageURLs(
    allProjectAdvisors.map((adId) => adId?.trim()),
  )

  const tbfData = await requestTbf<
    TrustedAdvisorQuery,
    TrustedAdvisorQueryVariables
  >(trustedAdvisorQuery, {
    adIds: allProjectAdvisors.filter(Boolean) as string[],
  })

  return {
    props: {
      data: { ...pageData.data, allEmployeesImageURLs },
      advisorsData: tbfData.data.getTrustedAdvisors,
      messages: (await import(`@/translation/${context.locale}.json`)).default,
      isPreview,
      allPages: await getAllPages(),
    },
  }
}

export default Project
